import { ReactElement, useMemo } from 'react'
import { getShapeObject, createMeshStandardMaterialMemoized } from '@scene'
import { DoubleSide, LineCurve3, MeshBasicMaterial, Vector3 } from 'three'
import { useTheme } from '@mui/material'
import { BasicMesh, getCenterMultipleReduceMean, ImmutableVector3 } from '@modugen/ui-toolkit'
import { useDisposeThreeResourceOnUnmount } from '@editorHooks'
import { getContrastTextColor } from 'src/utils/color'
import { areaLoadForceFormatter, toLoadSceneTextLines } from '../LoadMesh/utils'
import { generateShapeEdgesFromPoints } from '../ShapeMesh/utils'
import Text3DThree from '../Text3DThree'
import { getLoadCategoryColor, highlightLoadColor } from '../loads'
import {
  AREA_LOAD_LABEL_GAP,
  AREA_LOAD_TEXT_SCALE,
  AREA_LOAD_TUBE_RADIUS,
  getAreaLoadExtrusion,
  getAreaLoadShapeMatrix,
  toAreaLoadPoints,
} from './utils'

const GLOBAL_TEXT_X = new Vector3(1, 0, 0)
const GLOBAL_TEXT_Y = new Vector3(0, 1, 0)

interface Props {
  load: AreaLoad
  onClick?: () => void
  isActive?: boolean
  color?: string
  opacity?: number
}

const AreaLoadMeshElement = ({
  load,
  onClick,
  isActive = false,
  color,
  opacity = 1,
}: Props): ReactElement | null => {
  const theme = useTheme()
  const points = useMemo(
    () => toAreaLoadPoints(load.area_of_attack?.points ?? []),
    [load.area_of_attack],
  )
  const { extrusionNormal, thickness } = useMemo(
    () => getAreaLoadExtrusion(points, load.force),
    [points, load.force],
  )
  const extrusionNormalImmutable = useMemo(
    () => new ImmutableVector3(extrusionNormal.x, extrusionNormal.y, extrusionNormal.z),
    [extrusionNormal],
  )

  const matrixTransform = useMemo(
    () => getAreaLoadShapeMatrix(points, extrusionNormal),
    [points, extrusionNormal],
  )

  const shapeObject = useMemo(
    () => (points.length < 3 ? null : getShapeObject(points, [], matrixTransform)),
    [points, matrixTransform],
  )

  const labelPosition = useMemo(() => {
    if (points.length < 3) return new ImmutableVector3()

    return getCenterMultipleReduceMean(points).addScaledVector(
      extrusionNormalImmutable,
      thickness + AREA_LOAD_LABEL_GAP,
    )
  }, [points, extrusionNormalImmutable, thickness])

  const loadColor = getLoadCategoryColor(load.load_case.category)
  const backgroundColor = isActive ? color || highlightLoadColor : color || loadColor
  const textColor = getContrastTextColor(backgroundColor)
  const label = useMemo(() => areaLoadForceFormatter(-load.force.z), [load.force.z])

  const shapeOpacity = (isActive ? 0.25 : 0.1) * opacity

  const allEdges = useMemo(() => {
    return generateShapeEdgesFromPoints(points, thickness, extrusionNormalImmutable)
  }, [points, thickness, extrusionNormalImmutable])

  const edgeCurves = useMemo(() => {
    return allEdges.map(([start, end]) => {
      return new LineCurve3(start.v, end.v)
    })
  }, [allEdges])

  const fillMaterial = useMemo(
    () =>
      createMeshStandardMaterialMemoized({
        side: DoubleSide,
        color: backgroundColor || theme.palette.grey[800],
        polygonOffset: true,
        polygonOffsetUnits: -100 + 1 * 10,
        opacity: shapeOpacity,
        transparent: true,
        depthWrite: false,
      }),
    [backgroundColor, theme, shapeOpacity],
  )

  const tubeMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: backgroundColor || theme.palette.grey[800],
        opacity,
        transparent: opacity < 1,
        depthWrite: opacity >= 1,
        toneMapped: false,
      }),
    [backgroundColor, theme, opacity],
  )

  useDisposeThreeResourceOnUnmount(tubeMaterial)

  const meshUserData = useMemo(
    () => ({
      type: 'area-load',
      contextMenuActions: {
        hide: { guid: load.guid },
        properties: { guid: load.guid },
      },
    }),
    [load.guid],
  )

  if (points.length < 3 || !shapeObject) return null

  return (
    <>
      <BasicMesh
        cursor="pointer"
        onClick={onClick}
        // why disabling matrixAutoUpdate should be fine:
        // https://github.com/pmndrs/react-three-fiber/issues/635#issuecomment-682439509
        matrix={matrixTransform}
        matrixAutoUpdate={false}
        material={fillMaterial}
        renderOrder={100}
        userData={meshUserData}
      >
        <extrudeGeometry
          args={[
            shapeObject,
            {
              depth: thickness,
              bevelEnabled: false,
            },
          ]}
        />
      </BasicMesh>

      {edgeCurves.map((curve, index) => (
        <mesh
          key={index}
          material={tubeMaterial}
          onClick={onClick}
          renderOrder={100}
          userData={meshUserData}
        >
          <tubeGeometry args={[curve, 4, AREA_LOAD_TUBE_RADIUS, 8, false]} />
        </mesh>
      ))}

      <Text3DThree
        textLines={toLoadSceneTextLines(label)}
        color={textColor || theme.palette.text.primary}
        backgroundColor={backgroundColor || (theme.scenePalette.highlight as string)}
        borderWidth={0.005}
        borderColor={textColor || theme.palette.text.primary}
        position={labelPosition.v}
        anchorLocation="center"
        scale={AREA_LOAD_TEXT_SCALE}
        opacity={opacity}
        onClick={onClick ? () => onClick() : undefined}
        textXOrientation={GLOBAL_TEXT_X}
        textYOrientation={GLOBAL_TEXT_Y}
        extrudeDepthSymmetrically
        textDepth={0.001}
      />
    </>
  )
}

export default AreaLoadMeshElement
